import { makeObservable, observable, action, runInAction, computed } from 'mobx';
import {
  searchModelPricing,
  addModelPricing,
  updateModelPricing,
  removeModelPricing,
  type AiModelPricing,
} from '@/api/modelPricing';
import type { RootStore } from './RootStore';

// Platform-level: pricing rates are not merchant-scoped. Append-only by
// convention — when rates change, INSERT a new row + set ExpirationDate on
// the prior row. The store exposes raw rows; the UI groups by ModelName.

export class ModelPricingStore {
  @observable items: AiModelPricing[] = [];
  @observable totalCount = 0;
  @observable isLoading = false;
  @observable error: string | null = null;

  @observable search = '';
  @observable showInactive = false;

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
  }

  @action setSearch(v: string): void { this.search = v; }
  @action setShowInactive(v: boolean): void { this.showInactive = v; }

  @action
  async fetch(): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const result = await searchModelPricing({ search: this.search, pageSize: 500 });
      runInAction(() => {
        this.items = result.data ?? [];
        this.totalCount = result.paging?.totalRecordCount ?? this.items.length;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load pricing';
      });
    }
  }

  @computed get filtered(): AiModelPricing[] {
    return this.items.filter((p) => (this.showInactive ? true : p.isActive));
  }

  // Group by (ModelName, Provider) and sort each group's rates by EffectiveDate desc.
  @computed get grouped(): { key: string; modelName: string; provider: string; rates: AiModelPricing[] }[] {
    const map = new Map<string, AiModelPricing[]>();
    for (const p of this.filtered) {
      const key = `${p.modelName}|${p.provider}`;
      const existing = map.get(key);
      if (existing) existing.push(p);
      else map.set(key, [p]);
    }
    const groups: { key: string; modelName: string; provider: string; rates: AiModelPricing[] }[] = [];
    for (const [key, rates] of map.entries()) {
      const [modelName, provider] = key.split('|');
      const sorted = [...rates].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
      groups.push({ key, modelName, provider, rates: sorted });
    }
    return groups.sort((a, b) => a.modelName.localeCompare(b.modelName));
  }

  @action
  async create(input: Partial<AiModelPricing>): Promise<AiModelPricing> {
    const created = await addModelPricing(input);
    void this.fetch();
    return created;
  }

  @action
  async update(input: Partial<AiModelPricing>): Promise<AiModelPricing> {
    const updated = await updateModelPricing(input);
    void this.fetch();
    return updated;
  }

  @action
  async remove(id: number): Promise<void> {
    await removeModelPricing(id);
    runInAction(() => {
      this.items = this.items.filter((p) => p.id !== id);
      this.totalCount = Math.max(0, this.totalCount - 1);
    });
  }
}
